import { useEffect, useState } from "react";
import { api, formatDate } from "../lib/api";
import { MODULE_ORDER, MODULE_SCHEMAS } from "../lib/moduleSchemas";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Link2, Copy, Ban, Trash2, Plus, ShieldCheck, Clock, Eye, Check,
  AlertTriangle, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

const EXPIRY_PRESETS = [
  { label: "24 hours", hours: 24 },
  { label: "72 hours", hours: 72 },
  { label: "7 days", hours: 24 * 7 },
  { label: "30 days", hours: 24 * 30 },
  { label: "90 days", hours: 24 * 90 },
];

export default function ShareLinks() {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [created, setCreated] = useState(null); // link shown one time

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/shares");
      setShares(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const revoke = async (id) => {
    if (!confirm("Revoke this share link? Anyone holding it will be locked out immediately.")) return;
    await api.post(`/shares/${id}/revoke`);
    toast.success("Revoked");
    await load();
  };
  const remove = async (id) => {
    if (!confirm("Delete this share record permanently?")) return;
    await api.delete(`/shares/${id}`);
    toast.success("Deleted");
    await load();
  };

  return (
    <div className="space-y-6" data-testid="shares-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-overline">Time-boxed, Revocable</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-1">
            Secure Family Share Links
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            Generate a read-only link to share chosen parts of your vault with a trusted
            family member. Scope it to specific modules, set an expiry, and revoke any time.
            Every access is logged.
          </p>
        </div>
        <Button onClick={() => setDialog(true)} data-testid="share-create-button">
          <Plus className="h-4 w-4 mr-2" /> New share link
        </Button>
      </div>

      {/* Security note */}
      <Card className="p-4 bg-amber-50 border-amber-200 flex items-start gap-3">
        <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
        <div className="text-sm text-amber-900">
          Anyone with the link can view the shared data until it expires or you revoke it.
          <strong className="ml-1">Send the link via a trusted channel only</strong>
          (personal message, encrypted email) — not public social or group chats.
        </div>
      </Card>

      {/* List */}
      {loading ? (
        <div className="py-12 text-center text-slate-500 text-sm">Loading...</div>
      ) : shares.length === 0 ? (
        <Card className="p-10 border-dashed text-center">
          <Link2 className="h-8 w-8 mx-auto text-slate-400" strokeWidth={1.5} />
          <div className="mt-3 font-medium">No share links yet</div>
          <div className="text-sm text-slate-500 mt-1">
            Create your first secure share link — scope it, set an expiry, send it.
          </div>
          <Button className="mt-6" onClick={() => setDialog(true)}>
            <Plus className="h-4 w-4 mr-2" /> New share link
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {shares.map((s) => {
            const expired = s.expired;
            const revoked = s.revoked;
            const dead = expired || revoked;
            return (
              <Card key={s.id} className={`p-5 ${dead ? "bg-slate-50" : ""}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {revoked ? (
                        <Badge variant="destructive" className="gap-1"><Ban className="h-3 w-3" />Revoked</Badge>
                      ) : expired ? (
                        <Badge variant="secondary" className="gap-1"><Clock className="h-3 w-3" />Expired</Badge>
                      ) : (
                        <Badge className="bg-emerald-600 hover:bg-emerald-600 gap-1"><ShieldCheck className="h-3 w-3" />Active</Badge>
                      )}
                      {s.reveal_sensitive
                        ? <Badge variant="outline" className="gap-1"><Eye className="h-3 w-3" />Unmasked</Badge>
                        : <Badge variant="outline">Masked</Badge>}
                    </div>
                    <div className="mt-2 font-heading font-semibold truncate">
                      {s.note || "Unnamed share"}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      Expires {formatDate(s.expires_at)} · Created {formatDate(s.created_at)}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xs text-slate-500 label-overline">Views</div>
                    <div className="font-heading text-2xl font-semibold tabular-nums">
                      {s.access_count || 0}
                    </div>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-1">
                  {(s.modules_allowed && s.modules_allowed.length > 0
                    ? s.modules_allowed
                    : ["all"]).slice(0, 6).map((m) => (
                    <Badge key={m} variant="secondary" className="text-[10px]">
                      {m === "all" ? "All modules" : MODULE_SCHEMAS[m]?.label || m}
                    </Badge>
                  ))}
                  {s.modules_allowed && s.modules_allowed.length > 6 && (
                    <Badge variant="secondary" className="text-[10px]">
                      +{s.modules_allowed.length - 6} more
                    </Badge>
                  )}
                </div>

                {s.last_accessed_at && (
                  <div className="mt-3 text-xs text-slate-500">
                    Last opened {formatDate(s.last_accessed_at)}
                  </div>
                )}

                <div className="mt-4 flex gap-2 justify-end">
                  {!dead && (
                    <Button size="sm" variant="outline" onClick={() => revoke(s.id)}
                      data-testid={`share-revoke-${s.id}`}>
                      <Ban className="h-3.5 w-3.5 mr-1" /> Revoke
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => remove(s.id)}
                    data-testid={`share-delete-${s.id}`}>
                    <Trash2 className="h-3.5 w-3.5 text-red-600" />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <CreateDialog
        open={dialog}
        onClose={() => setDialog(false)}
        onCreated={(c) => { setCreated(c); setDialog(false); load(); }}
      />
      <CreatedDialog
        created={created}
        onClose={() => setCreated(null)}
      />
    </div>
  );
}

function CreateDialog({ open, onClose, onCreated }) {
  const [note, setNote] = useState("");
  const [hours, setHours] = useState(168);
  const [reveal, setReveal] = useState(false);
  const [includePlan, setIncludePlan] = useState(true);
  const [includeContacts, setIncludeContacts] = useState(false);
  const [modules, setModules] = useState([]); // empty = all
  const [busy, setBusy] = useState(false);

  const toggleMod = (slug) => {
    setModules((m) => m.includes(slug) ? m.filter((x) => x !== slug) : [...m, slug]);
  };
  const allModules = modules.length === 0;

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/shares", {
        note: note || null,
        expires_in_hours: hours,
        reveal_sensitive: reveal,
        include_action_plan: includePlan,
        include_emergency_contacts: includeContacts,
        modules_allowed: allModules ? [] : modules,
      });
      setNote(""); setHours(168); setReveal(false);
      setIncludePlan(true); setIncludeContacts(false); setModules([]);
      onCreated(data);
      toast.success("Share link created");
    } catch {
      toast.error("Could not create share link");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create secure share link</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Name this share (who is it for?)</Label>
            <Input placeholder="e.g., For Priya — emergency access"
              value={note} onChange={(e) => setNote(e.target.value)}
              data-testid="share-note-input" />
          </div>

          <div>
            <Label>Expires in</Label>
            <div className="flex gap-2 flex-wrap mt-2">
              {EXPIRY_PRESETS.map((p) => (
                <Button
                  key={p.hours}
                  type="button"
                  size="sm"
                  variant={hours === p.hours ? "default" : "outline"}
                  onClick={() => setHours(p.hours)}
                  data-testid={`share-expiry-${p.hours}`}
                >
                  {p.label}
                </Button>
              ))}
            </div>
          </div>

          <div className="border-t border-slate-200 pt-4 space-y-3">
            <div className="flex items-start gap-3">
              <Switch checked={reveal} onCheckedChange={setReveal} data-testid="share-reveal-switch" />
              <div className="min-w-0">
                <div className="text-sm font-medium">Reveal sensitive values</div>
                <div className="text-xs text-slate-500">
                  By default account numbers, PAN, policy numbers are masked (e.g. ••••6789).
                  Enable this only when the recipient explicitly needs to read those values.
                </div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Switch checked={includePlan} onCheckedChange={setIncludePlan} />
              <div>
                <div className="text-sm font-medium">Include Emergency Action Plan</div>
                <div className="text-xs text-slate-500">The 24h/7d/30d checklist and personal message.</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Switch checked={includeContacts} onCheckedChange={setIncludeContacts} />
              <div>
                <div className="text-sm font-medium">Include Emergency Contacts list</div>
                <div className="text-xs text-slate-500">So the recipient knows who else can help.</div>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <Label>Which modules to share</Label>
            <div className="text-xs text-slate-500 mt-1">
              Nothing selected means <strong>all 16 modules</strong>. Select specific ones to narrow the scope.
            </div>
            <div className="grid grid-cols-2 gap-2 mt-3 max-h-48 overflow-y-auto p-2 border border-slate-200 rounded-md">
              {MODULE_ORDER.map((slug) => (
                <label key={slug} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={modules.includes(slug)}
                    onCheckedChange={() => toggleMod(slug)}
                  />
                  {MODULE_SCHEMAS[slug].label}
                </label>
              ))}
            </div>
            {!allModules && (
              <div className="mt-2 text-xs text-slate-600">
                Scoped to {modules.length} module{modules.length === 1 ? "" : "s"}.
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={create} disabled={busy} data-testid="share-submit-button">
            {busy ? "Creating..." : "Create link"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreatedDialog({ created, onClose }) {
  const copy = async () => {
    if (!created?.url) return;
    try {
      await navigator.clipboard.writeText(created.url);
      toast.success("Copied to clipboard");
    } catch {}
  };
  const open = () => {
    if (created?.url) window.open(created.url, "_blank", "noopener");
  };
  return (
    <Dialog open={!!created} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Check className="h-5 w-5 text-emerald-600" /> Share link ready
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            This link is shown only once. Copy it now and send via a trusted channel.
          </p>
          <div className="flex gap-2">
            <Input value={created?.url || ""} readOnly
              className="font-mono text-xs"
              data-testid="share-created-url" />
            <Button onClick={copy} data-testid="share-copy-url-button">
              <Copy className="h-4 w-4 mr-1" /> Copy
            </Button>
          </div>
          <div className="text-xs text-slate-500">
            Expires {created?.expires_at ? new Date(created.expires_at).toLocaleString() : ""}
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" onClick={open}>
              <ExternalLink className="h-4 w-4 mr-1" /> Preview
            </Button>
            <Button onClick={onClose} data-testid="share-created-done-button">Done</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
